// Confirmación de «Cancelar venta»: pide el motivo, cancela la sesión y sale a Mis ventas.
// Cada error trae su salida: seguir con la venta, reintentar o releer la venta.
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { ConfirmDialog, Input } from '@/kit';
import { useCancelSession, useWizardSession } from '../api/useWizard';
import {
    CANCEL_REASON_MAX_LENGTH,
    CANCEL_SALE_ACTION_LABEL,
    cancelSaleAction,
    cancelSaleErrorMessage,
    cancelSaleImpact,
    cancelSaleSuccessMessage,
    isValidCancelReason,
} from '../cancelSale';
import {
    ACCOUNT_OPENING_CANCEL,
    ACCOUNT_OPENING_CANCEL_IMPACT,
    ACCOUNT_OPENING_CANCEL_KEEP,
    ACCOUNT_OPENING_CANCEL_REASON_PLACEHOLDER,
    ACCOUNT_OPENING_CANCELLED,
} from '../accountOpening';
import { stepAfterCheckout } from '../steps';

/** El aviso del reembolso se queda más tiempo: el vendedor tiene que decírselo al cliente. */
const REFUND_TOAST_MS = 15_000;

export function CancelSaleDialog({
    sessionId,
    open,
    onClose,
    accountOpening = false,
}: {
    sessionId: string;
    open: boolean;
    onClose: () => void;
    accountOpening?: boolean;
}) {
    const navigate = useNavigate();
    const cancelSession = useCancelSession();
    const { data: session, refresh } = useWizardSession(sessionId);
    const impact = accountOpening
        ? { locked: false, message: ACCOUNT_OPENING_CANCEL_IMPACT }
        : cancelSaleImpact(session);
    const title = accountOpening ? ACCOUNT_OPENING_CANCEL : 'Cancelar venta';
    const [reason, setReason] = useState('');

    const submit = async (): Promise<void> => {
        const trimmed = reason.trim();
        if (!isValidCancelReason(trimmed)) return;
        try {
            const result = await cancelSession.mutateAsync({ sessionId, reason: trimmed });
            const hasRefund = (result.refundPendingCents ?? 0) > 0;
            const message = accountOpening
                ? ACCOUNT_OPENING_CANCELLED
                : cancelSaleSuccessMessage(result);
            toast.success(message, {
                duration: hasRefund ? REFUND_TOAST_MS : undefined,
            });
            onClose();
            await navigate({ to: '/sales' });
        } catch (error) {
            reportError(error);
        }
    };

    const reportError = (error: unknown): void => {
        const message = cancelSaleErrorMessage(error);
        const action = cancelSaleAction(error);
        // Con reintento el diálogo sigue abierto y conserva el motivo.
        if (action !== 'retry') onClose();
        if (action === undefined) {
            toast.error(message);
            return;
        }
        const onClick = (): void => {
            if (action === 'retry') void submit();
            else if (action === 'reload') void refresh();
            else
                void navigate({
                    to: '/wizard/s/$sessionId/$step',
                    params: { sessionId, step: stepAfterCheckout(session) },
                });
        };
        toast.error(message, { action: { label: CANCEL_SALE_ACTION_LABEL[action], onClick } });
    };

    return (
        <ConfirmDialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose();
            }}
            title={title}
            description={impact.message}
            confirmLabel={title}
            cancelLabel={accountOpening ? ACCOUNT_OPENING_CANCEL_KEEP : 'Seguir con la venta'}
            destructive
            loading={cancelSession.isPending}
            confirmDisabled={impact.locked || !isValidCancelReason(reason)}
            onConfirm={() => void submit()}
        >
            {!impact.locked && (
                <Input
                    label="Motivo"
                    required
                    value={reason}
                    maxLength={CANCEL_REASON_MAX_LENGTH}
                    placeholder={
                        accountOpening
                            ? ACCOUNT_OPENING_CANCEL_REASON_PLACEHOLDER
                            : 'Por qué se cancela la venta'
                    }
                    onChange={(e) => {
                        setReason(e.target.value);
                    }}
                />
            )}
        </ConfirmDialog>
    );
}
